import { Grid, GridItem, Table } from '@flos/react-ui';
import { type PropsWithChildren } from 'react';
import Currency from '../../../shared_components/Currency/Currency';
import React from 'react';
import { useHippoText } from '../../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';

interface CoveragePriceOPCLabels {
    title: string;
    subtitle?: string;
    priceChangeSubTitle: string;
    coverablePriceBefore: string;
    coverablePriceAfter: string;
}
const CoveragePriceOPC = ({
    children,
    hasChildren,
    labels,
    coveragePrice,
    oldTotalPrice,
    newTotalPrice,
    currencyPostfix,
    currencyPriceChangePrefix,
    isChildrenPositionBeforePrice = true,
    hasChangeEffectImmediately = false
}: PropsWithChildren<{
    hasChildren?: boolean;
    labels: CoveragePriceOPCLabels;
    coveragePrice: number;
    oldTotalPrice: number;
    newTotalPrice: number;
    currencyPostfix: string;
    currencyPriceChangePrefix?: string;
    isChildrenPositionBeforePrice?: boolean;
    hasChangeEffectImmediately?: boolean;
}>): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { productName } = useRecoilValue(opcInfoAtom);
    const showChoTitle = productName === 'top_cho_insurance';
    return (
        <>
            <h4 className="u-spacing-inset-m--top">{labels.title}</h4>
            <Grid gap={7}>
                {labels.subtitle && (
                    <GridItem span={12}>
                        <p className="u-spacing-stack-none">{labels.subtitle}</p>
                    </GridItem>
                )}
                {!isChildrenPositionBeforePrice && hasChildren ? (
                    <GridItem span={12}>{children}</GridItem>
                ) : (
                    <></>
                )}
                <GridItem span={12}>
                    <h6 className="u-spacing-stack-none">
                        {currencyPriceChangePrefix}
                        <Currency price={coveragePrice} paymentFrequencyPostfix={currencyPostfix} />
                    </h6>
                </GridItem>
                {isChildrenPositionBeforePrice && hasChildren ? (
                    <GridItem span={12}>{children}</GridItem>
                ) : (
                    <></>
                )}
                {hasChangeEffectImmediately && !showChoTitle && (
                    <GridItem span={12}>
                        {getHippoText('DSS-ADD-SIMPLE-COVERAGE-START-DATE-PLACEHOLDER')}
                    </GridItem>
                )}
                {showChoTitle && (
                    <GridItem span={12}>{getHippoText('DSS-CHANGE-COVERAGE-CHO-TEXT')}</GridItem>
                )}
                <GridItem span={12}>
                    <h6>{labels.priceChangeSubTitle}</h6>
                    <Table mobileDirection="vertical" wrapped={true}>
                        <Table.Head key={'removeCoverageHeaderGroup'}>
                            <Table.Row>
                                <Table.HeadCell
                                    key={'removeCoverageHeaderBefore'}
                                    id={'table-head-before'}
                                >
                                    {labels.coverablePriceBefore}
                                </Table.HeadCell>
                                <Table.HeadCell
                                    key={'removeCoverageHeaderAfter'}
                                    id={'table-head-after'}
                                >
                                    {labels.coverablePriceAfter}
                                </Table.HeadCell>
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell
                                    data-header={labels.coverablePriceBefore}
                                    scope="row"
                                    key={'removeCoverageHeaderBefore'}
                                    id={'table-head-before'}
                                >
                                    <Currency
                                        price={oldTotalPrice}
                                        paymentFrequencyPostfix={currencyPostfix}
                                        displayZeroValue={true}
                                    />
                                </Table.Cell>
                                <Table.Cell
                                    data-header={labels.coverablePriceAfter}
                                    scope="row"
                                    key={'removeCoverageHeaderAfter'}
                                    id={'table-head-after'}
                                >
                                    <Currency
                                        price={newTotalPrice}
                                        paymentFrequencyPostfix={currencyPostfix}
                                        displayZeroValue={true}
                                    />
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </GridItem>
            </Grid>
        </>
    );
};

export { CoveragePriceOPC, type CoveragePriceOPCLabels };
